/**
 * Navigation Component
 * Header navigation with conditional rendering and robust logo loader.
 * This file attempts the exact RJBD logo path first and falls back progressively.
 * The logo now uses object-cover so it fills the circular logo area (may crop edges).
 */

import { Link, useLocation } from 'react-router';
import React, { useEffect, useState } from 'react';
import { Users, BarChart3, User, LogIn, UserPlus, LogOut, CreditCard, Info, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { API_CONFIG } from '../config/api.config';
import ContactUsModal from './ContactUsModal';
import AboutRJBDModal from './AboutRJBDModal';

/**
 * NavigationProps interface
 * @description Props for the Navigation component (reserved for future use).
 */
interface NavigationProps {}

/**
 * Navigation component
 * @description Top navigation bar used across the app. Tries to load the exact logo file
 * (/images/RJBD.png) first and uses fallback candidates if it fails. The logo now
 * fills the circular area using object-cover for a full-bleed visual.
 *
 * Visual behavior:
 * - The logo is shown inside a circular wrapper sized to match nav height.
 * - object-cover is used so the image fills the circle (edges may be cropped).
 * - A subtle fade+scale entrance occurs when the logo is ready.
 *
 * @param _props - NavigationProps (unused)
 * @returns JSX.Element
 */
const Navigation: React.FC<NavigationProps> = (_props) => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [userData, setUserData] = useState<any>(null);
  const [logoLoaded, setLogoLoaded] = useState<boolean>(false);
  const [logoSrc, setLogoSrc] = useState<string | null>(null);
  const [showContactModal, setShowContactModal] = useState<boolean>(false);
  const [showAboutModal, setShowAboutModal] = useState<boolean>(false);

  /**
   * Candidate logo paths attempted in order.
   * Starts with backend URL for reliability, then local path, then CDN fallback.
   */
  const BACKEND_ORIGIN = API_CONFIG.getBackendOrigin();
  const PUBLIC_LOGO_PATH = '/public/images/PNLogo.png';
  const LOGO_CANDIDATES = [
    PUBLIC_LOGO_PATH, // Public folder path directly
    `${BACKEND_ORIGIN}/public/images/PNLogo.png`, // Backend fallback with public path
    '/images/PNLogo.png', // Legacy root-relative path
    'https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/8b6e4183-af01-479c-a603-5231a26c498e.png', // CDN fallback
  ];
  /**
   * Attempt to load candidate images sequentially until one succeeds.
   * Prevents persistent 404s and reduces layout shift.
   */
  useEffect(() => {
    let cancelled = false;
    let idx = 0;

    const tryNext = () => {
      if (cancelled) return;
      if (idx >= LOGO_CANDIDATES.length) {
        setLogoSrc(LOGO_CANDIDATES[LOGO_CANDIDATES.length - 1]);
        setLogoLoaded(true);
        return;
      }

      const candidate = LOGO_CANDIDATES[idx++];
      const img = new Image();
      img.src = candidate;
      img.onload = () => {
        if (cancelled) return;
        setLogoSrc(candidate);
        setLogoLoaded(true);
        console.log('Navigation: logo loaded from', candidate);
      };
      img.onerror = () => {
        console.warn('Navigation: failed to load', candidate, '— trying next candidate');
        tryNext();
      };
    };

    tryNext();

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Check auth status and user info on route changes so the nav can show welcome text
   */
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const user = localStorage.getItem('userData');
    setIsAuthenticated(!!token);
    if (user) {
      try {
        const parsed = JSON.parse(user);
        setUserData(parsed);
        // Helpful debug logs during development
        console.log('🔍 Navigation user data:', parsed);
        console.log('🔍 Navigation profession:', {
          profession: parsed?.profession,
          basicDetailsProfession: parsed?.basicDetails?.profession,
        });
      } catch (err) {
        console.warn('Navigation: failed to parse userData', err);
      }
    }
  }, [location]);

  /**
   * Logout handler - clears local storage and navigates to home.
   */
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    setIsAuthenticated(false);
    setUserData(null);
    window.location.href = '/';
  };

  return (
    <nav className="bg-gradient-to-r from-[#0b3b82] to-[#ff2d87] shadow-lg border-b border-[#d4a017] fixed top-0 left-0 right-0 z-50">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16">
          {/* Logo & Brand - Leftmost */}
          <Link to="/" className="flex items-center space-x-3 mr-8" aria-label="Partner Nest Home">
            {/**
             * Logo wrapper:
             * - Responsive sizes (h-12..md:h-16) to match nav height.
             * - rounded-full mask, border and shadow retained.
             * - No inner padding so object-cover fills the circle.
             */}
            <div
              className={
                'h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 bg-gradient-to-br from-white to-[#fff3f6] rounded-full flex items-center justify-center border-2 border-white shadow-lg overflow-hidden ' +
                (logoLoaded
                  ? 'opacity-100 scale-100 transition-transform transition-opacity duration-300 ease-out'
                  : 'opacity-0 scale-95 transition-transform transition-opacity duration-300 ease-out')
              }
            >
              {/**
               * The image uses object-cover so it fills the circular area (may crop).
               * width/height attributes help the browser reserve the correct space early.
               */}
              <img
                src={logoSrc ?? PUBLIC_LOGO_PATH}
                alt="Partner Nest Logo"
                width={64}
                height={64}
                className="w-full h-full object-cover object-center"
                decoding="async"
                loading="lazy"
                onError={(e) => {
                  // Final safe fallback if something unhandled happens.
                  const imgEl = e.target as HTMLImageElement;
                  if (!imgEl.dataset.fallback) {
                    imgEl.dataset.fallback = '1';
                    imgEl.src = PUBLIC_LOGO_PATH;
                    console.warn('Navigation: image onError fallback applied to public path');
                  }
                }}
              />
            </div>

            <div>
              <h1 className="text-xl font-bold text-white font-serif">Partner Nest</h1>
              <p className="text-xs text-[#ffd9a6] -mt-1">Brining Hearts Together</p>
            </div>
          </Link>

          {/* Desktop Links - Centered */}
          <div className="hidden md:flex items-center space-x-8 flex-1 justify-center">
                            <button
                  onClick={() => setShowAboutModal(true)}
                  className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors text-[#ffd9a6] hover:text-white hover:bg-[#0b3b82]"
                >
                  <Info className="w-4 h-4" />
                  <span>About Partner Nest</span>
                </button>
            {isAuthenticated ? (
              <>


                <Link
                  to="/search"
                  className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname === '/search'
                      ? 'text-white bg-[#0b3b82]'
                      : 'text-[#ffd9a6] hover:text-white hover:bg-[#0b3b82]'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  <span>Find Partners</span>
                </Link>

                <Link
                  to="/dashboard"
                  className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname === '/dashboard'
                      ? 'text-white bg-[#0b3b82]'
                      : 'text-[#ffd9a6] hover:text-white hover:bg-[#0b3b82]'
                  }`}
                >
                  <BarChart3 className="w-4 h-4" />
                  <span>Dashboard</span>
                </Link>

                <Link
                  to="/payment"
                  className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname === '/payment'
                      ? 'text-white bg-[#0b3b82]'
                      : 'text-[#ffd9a6] hover:text-white hover:bg-[#0b3b82]'
                  }`}
                >
                  <CreditCard className="w-4 h-4" />
                  <span>Plans</span>
                </Link>

                <Link
                  to="/profile"
                  className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname === '/profile'
                      ? 'text-white bg-[#0b3b82]'
                      : 'text-[#ffd9a6] hover:text-white hover:bg-[#0b3b82]'
                  }`}
                >
                  <User className="w-4 h-4" />
                  <span>Profile</span>
                </Link>


              </>
            ) : null}
                <button
                  onClick={() => setShowContactModal(true)}
                  className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors text-[#ffd9a6] hover:text-white hover:bg-[#0b3b82]"
                >
                  <Mail className="w-4 h-4" />
                  <span>Contact Us</span>
                </button>
          </div>

          {/* Auth Buttons & Welcome */}
          <div className="flex items-center space-x-4">
            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <div className="text-sm text-[#ffd9a6]">
                  Welcome,{ ' ' }
                  {userData?.basicDetails?.firstName
                    ? `${userData.basicDetails.firstName} ${userData.basicDetails.lastName || ''}`.trim()
                    : userData?.firstName
                    ? `${userData.firstName} ${userData.lastName || ''}`.trim()
                    : userData?.name || 'User'}
                </div>
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  className="bg-transparent border-white text-white hover:bg-white hover:text-[#ff2d87]"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            ) : (
              <>
                <Link to="/login">
                  <Button className="bg-transparent border-white text-white hover:bg-white hover:text-[#ff2d87]" variant="outline">
                    <LogIn className="w-4 h-4 mr-2" />
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-white hover:bg-[#fff6f9] text-[#ff2d87] border-2 border-white">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Register
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden bg-gradient-to-r from-[#0b3b82] to-[#ff2d87] border-t border-[#d4a017] fixed top-16 left-0 right-0 z-50">
        <div className="px-2 pt-2 pb-3 space-y-1">
          {isAuthenticated ? (
            <>
              <Link
                to="/search"
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium ${
                  location.pathname === '/search' ? 'text-white bg-[#0b3b82]' : 'text-[#ffd9a6] hover:text-white hover:bg-[#0b3b82]'
                }`}
              >
                <Users className="w-4 h-4" />
                <span>Find Partners</span>
              </Link>
              <Link
                to="/dashboard"
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium ${
                  location.pathname === '/dashboard' ? 'text-white bg-[#0b3b82]' : 'text-[#ffd9a6] hover:text-white hover:bg-[#0b3b82]'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                <span>Dashboard</span>
              </Link>
              <Link
                to="/payment"
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium ${
                  location.pathname === '/payment' ? 'text-white bg-[#0b3b82]' : 'text-[#ffd9a6] hover:text-white hover:bg-[#0b3b82]'
                }`}
              >
                <CreditCard className="w-4 h-4" />
                <span>Plans</span>
              </Link>
              <Link
                to="/profile"
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium ${
                  location.pathname === '/profile' ? 'text-white bg-[#0b3b82]' : 'text-[#ffd9a6] hover:text-white hover:bg-[#0b3b82]'
                }`}
              >
                <User className="w-4 h-4" />
                <span>Profile</span>
              </Link>
            </>
          ) : null}
        </div>
      </div>

      {/* Contact Us Modal */}
      <ContactUsModal open={showContactModal} onClose={() => setShowContactModal(false)} />
      
      {/* About Partner Nest Modal */}
      <AboutRJBDModal open={showAboutModal} onClose={() => setShowAboutModal(false)} />
    </nav>
  );
};

export default Navigation;